import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, switchMap } from 'rxjs';
import { AlertHandler } from 'src/app/core/handlers/alert-handler';
import { Scenario } from 'src/app/core/models/scenario';
import { MessageHandler } from 'src/app/shared/handlers/message-handler';
import { PrecisionType } from 'src/app/shared/interfaces/chart.model';
import { SupplyMode } from 'src/app/shared/interfaces/global-filter-model';
import { GlobalFilterService } from 'src/app/shared/services/global-filter.service';
import { MondelezDataService } from 'src/app/shared/services/mondelez-data.service';
import { MondelezService } from 'src/app/shared/services/mondelez.service';

@Component({
  selector: 'app-map-view-dialog-logistics',
  templateUrl: './map-view-dialog-logistics.component.html',
  styleUrls: ['./map-view-dialog-logistics.component.scss'],
})
export class MapViewDialogLogisticsComponent {
  public scenarioId: number;
  public yearRange = {
    startYear: undefined,
    endYear: undefined,
  };
  public kpiData: any;
  public kpiCardValues = {};
  public selectedFilters: any;
  public tableHeader = [];
  public spaceTableData = [];
  public throughputTableData = [];
  public masterData: any;
  public cardPanelOpen = true;

  private iteration_code: number;
  private subs = new Subscription();

  constructor(
    private mondelezService: MondelezService,
    private mondelezDataService: MondelezDataService,
    public dialogRef: MatDialogRef<MapViewDialogLogisticsComponent>,
    private globalFilterService: GlobalFilterService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.globalFilterService.globalFilters.subscribe((filter) => {
        if (filter) {
          this.yearRange.startYear = filter.start_year;
          this.yearRange.endYear = filter.end_year;
        }
      })
    );
    this.subs.add(
      this.mondelezDataService.currentScenarioChanged
        .pipe(
          switchMap((res) => {
            this.scenarioId = res.scenario_id;
            this.iteration_code = res.iteration_code;
            return this.globalFilterService.mapViewFilters;
          })
        )
        .subscribe((value: any) => {
          this.selectedFilters = value;
          if (this.scenarioId) {
            this.fetchData();
            // this.setDummyKpiData();
            // this.setKpiCardValues();
            // this.setDummyTableData();
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onClose(): void {
    // this.subs.unsubscribe();
    this.dialogRef.close();
  }

  fetchData(): void {
    let payload = {
      filters: {
        region: this.selectedFilters.geography,
        start_year: this.yearRange.startYear,
        end_year: this.yearRange.endYear,
        category: null,
        brand: null,
        manufacturing_network: null,
        node_code: this.selectedFilters.node_code ? [this.selectedFilters.node_code] : null,
      },
      scenario_id: this.scenarioId,
      supply_mode: SupplyMode.LOGISTICS,
      iteration_code: this.iteration_code,
    };
    this.subs.add(
      this.mondelezService.getMapViewDialogData(payload).subscribe({
        next: (result: any) => {
          if (result && result.response === 'SUCCESS') {
            this.masterData = result;
            this.kpiData = result.headers;
            this.setKpiCardValues();
            this.initTableData();
          } else {
            AlertHandler.ErrorAlert(MessageHandler.INTERNAL_ERROR);
          }
        },
        error: (error) => {
          AlertHandler.ErrorAlert(MessageHandler.INTERNAL_ERROR);
        },
      })
    );
  }

  private setKpiCardValues(): void {
    const properties = [
      { key: 'capacity_required', precision: PrecisionType.THOUSANDTH },
      { key: 'total_capacity', precision: PrecisionType.THOUSANDTH },
      { key: 'inc_capacity', precision: PrecisionType.THOUSANDTH },
      { key: 'pallet_position', precision: PrecisionType.THOUSANDTH },
      { key: 'capex', precision: PrecisionType.MILLIONTH },

      { key: 'inc_inventory', precision: PrecisionType.THOUSANDTH },
      { key: 'final_throughput', precision: PrecisionType.MILLIONTH },
      { key: 'inc_throughput', precision: PrecisionType.MILLIONTH },
    ];
    properties.forEach((property) => {
      this.kpiCardValues[property.key] = this.formatNumbers(this.kpiData[property.key], property.precision);
    });
  }

  private formatNumbers(amt: number, precision: PrecisionType = PrecisionType.MILLIONTH) {
    if (!amt) return 0;
    let millionth = amt / precision;
    return parseFloat(millionth.toFixed(1));
  }

  private initTableData(): void {
    this.setTableHeaders();
    this.masterData.space.length > 0;
    this.spaceTableData =
      this.masterData.space.length == 0
        ? []
        : [
            {
              node_type: 'capacity_required',
              data_key: 'capacity_required',
              code: "Capacity Required ('000 pp)",
              precision: PrecisionType.THOUSANDTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'utilization',
              data_key: 'utilization',
              code: 'Utilization (%)',
              precision: PrecisionType.NONE,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'total_capacity',
              data_key: 'total_capacity',
              code: "Total Capacity ('000 pp)",
              precision: PrecisionType.THOUSANDTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'inc_capacity',
              data_key: 'inc_capacity',
              code: "Incremental Capacity ('000 pp)",
              precision: PrecisionType.THOUSANDTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'pallet_position',
              data_key: 'pallet_position',
              code: "Incremental Pallet Positions ('000 pp)",
              precision: PrecisionType.THOUSANDTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'capex',
              data_key: 'capex',
              code: 'CAPEX ($MM)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'required_inventory',
              data_key: 'required_inventory',
              code: 'Inventory (MMlbs)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
          ];
    this.throughputTableData =
      this.masterData.throughput.length == 0
        ? []
        : [
            {
              node_type: 'required_throughput',
              data_key: 'required_throughput',
              code: 'Throughput Required (MM Cases)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'final_throughput',
              data_key: 'final_throughput',
              code: 'Throughput capacity (MM Cases)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
            {
              node_type: 'inc_throughput',
              data_key: 'inc_throughput',
              code: 'Incremental Throughput (MM Cases)',
              precision: PrecisionType.MILLIONTH,
              isExpanded: false,
              level: 1,
              plants: [],
            },
          ];
    const data = this.masterData.space; // To do data processing if required

    data.forEach((el, id) => {
      this.spaceTableData.forEach((row, index) => {
        const periodKey = el.period.replace(' ', '_');
        row[periodKey] = this.formatNumbers(el[row.data_key], row.precision);
        row['uid'] = id + index;
      });
    });

    const throughput = this.masterData.throughput; // To do data processing if required
    throughput.forEach((el, id) => {
      this.throughputTableData.forEach((row, index) => {
        const periodKey = el.period.replace(' ', '_');
        row[periodKey] = this.formatNumbers(el[row.data_key], row.precision);
      });
    });
  }

  setTableHeaders(): void {
    this.tableHeader = [];
    for (let year = this.yearRange.startYear; year <= this.yearRange.endYear; year++) {
      this.tableHeader.push(String(year));
      ['Q1_', 'Q2_', 'Q3_', 'Q4_'].forEach((el) => {
        this.tableHeader.push(el + year);
      });
    }
  }
}
